import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   CreditCardIcon,
} from '@forwardflow/icons'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import {
   CardElement,
   Elements,
   useElements,
   useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { PricingSummary } from '@forwardflow/modules/onboarding/components'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { CheckCircle02 } from '@forwardflow/icons/CheckCircle02'
import { ENV } from '@forwardflow/helpers/configs'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import {
   EAC_COLD_LEAD,
   EAC_NEW_LEAD,
} from '@forwardflow/constants/onboarding.constants'

const stripePromise = loadStripe(ENV.STRIPE_ID)

const PaymentDetailsForm = () => {
   const {
      getNextStep,
      getPreviousStep,
      onboardingData,
      setIsSubscriptionActivated,
   } = useOnboardingStoreV2()
   const { selectedTier, isSubscriptionActivated, pricingData } = onboardingData
   const [isTermsAccepted, setIsTermsAccepted] = useState(false)
   const [isPolicyAccepted, setIsPolicyAccepted] = useState(false)
   const [isTokenUsagePolicyAccepted, setIsTokenUsagePolicyAccepted] =
      useState(false)
   const [values, setValues] = useState<{
      nameOnCard: string
      city: string
      billingZIPCode: string
      couponCode: string
   }>({
      nameOnCard: '',
      city: '',
      billingZIPCode: '',
      couponCode: '',
   })
   const [isLoading, setIsLoading] = useState(false)
   const stripe = useStripe()
   const elements = useElements()

   const handleSubmit = async () => {
      if (isOperatorEmail(onboardingData?.email || '')) {
         getNextStep()
         return
      }
      if (!stripe || !elements || !elements?.getElement(CardElement)) {
         // Stripe.js has not yet loaded.
         // Make sure to disable form submission until Stripe.js has loaded.
         return
      }

      try {
         setIsLoading(true)

         const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements: elements,
            params: {
               billing_details: {
                  name: values.nameOnCard,
                  address: {
                     city: values?.city,
                     postal_code: values?.billingZIPCode,
                  },
               },
            },
         })

         if (error) {
            setIsLoading(false)
            return
         }

         const response = await OnboardingV2Service.subscribeToPackage({
            paymentInfo: {
               cardId:
                  ENV.NODE_ENV === 'development' || ENV.NODE_ENV === 'staging'
                     ? 'pm_card_visa'
                     : paymentMethod.id,
               nameOnCard: values.nameOnCard,
               city: values.city,
               postalCode: values.billingZIPCode,
               country: 'USA',
               addressLineOne: '123 Main St',
            },
            billingPeriod: 'monthly',
            tierId: Number(pricingData?.selectedTierId ?? 0),
            seatsCount: pricingData?.seatsCount ?? 1,
            couponCode: values.couponCode || '',
            newLeadBudget: pricingData?.newLeadCount * EAC_NEW_LEAD,
            reactivateLeadBudget:
               pricingData?.reactivateLeadCount * EAC_COLD_LEAD,
         })

         if (response?.success) {
            setIsSubscriptionActivated(true)
         }
      } catch (error) {
         // console.error('Error creating payment method', error)
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <Stack gap={48}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               Great! Let's get you subscribed so we can start working together.
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               I've selected the perfect plan based on your team size. You can
               adjust anytime.
            </p>
         </ContentCard>
         <Stack gap={16}>
            {selectedTier && (
               <ContentCard
                  className="!border-none !p-0"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
               >
                  <PricingSummary />
               </ContentCard>
            )}
            {isSubscriptionActivated ? (
               <ContentCard
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl border border-[#00BC7D]/30 bg-[rgba(0,188,125,0.05)]"
               >
                  <Group>
                     <CheckCircle02 color="#00D492" width={32} />
                     <Stack gap={2}>
                        <p className="text-[#D0FAE5]/90 font-medium">
                           Plan Subscription Activated
                        </p>
                     </Stack>
                  </Group>
               </ContentCard>
            ) : (
               <>
                  {' '}
                  <ContentCard
                     className="space-y-4"
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.6 }}
                  >
                     <Group>
                        <CreditCardIcon width={20} color="#A684FF" />
                        <p className="text-white font-medium">
                           Payment Details
                        </p>
                     </Group>

                     {/* Wrapped CardElement to match your TextInput design */}
                     <div className="relative">
                        <label className="text-white/90 font-medium mb-1.5 block text-sm">
                           Card Information
                        </label>
                        <div className="relative border rounded-lg border-white/20 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.05] transition-all">
                           <CardElement
                              className="py-2.5 px-3.5"
                              options={{
                                 style: {
                                    base: {
                                       fontSize: '14px',
                                       color: '#9ca3ae',
                                       fontFamily:
                                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                       '::placeholder': {
                                          color: 'rgba(156, 163, 175, 0.6)',
                                       },
                                       ':focus': {
                                          color: '#ffffff',
                                       },
                                    },
                                    invalid: {
                                       color: '#ef4444',
                                       iconColor: '#ef4444',
                                    },
                                    complete: {
                                       color: '#9ca3ae',
                                    },
                                 },
                                 hidePostalCode: true,
                              }}
                           />
                        </div>
                     </div>

                     <TextInput
                        label="Cardholder Name"
                        placeholder="John Doe"
                        name="nameOnCard"
                        value={values.nameOnCard}
                        onChange={(e) =>
                           setValues({ ...values, nameOnCard: e.target.value })
                        }
                     />

                     <Grid>
                        <Grid.Col span={6}>
                           <TextInput
                              name="city"
                              placeholder="City"
                              label="City"
                              value={values.city}
                              onChange={(e) =>
                                 setValues({ ...values, city: e.target.value })
                              }
                           />
                        </Grid.Col>
                        <Grid.Col span={6}>
                           <TextInput
                              label="Billing ZIP Code"
                              placeholder="eg: 94107"
                              name="billingZIPCode"
                              value={values.billingZIPCode}
                              onChange={(e) => {
                                 const numericValue = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 5)
                                 setValues({
                                    ...values,
                                    billingZIPCode: numericValue,
                                 })
                              }}
                           />
                        </Grid.Col>
                     </Grid>

                     <TextInput
                        label="Have a coupon code?"
                        placeholder="eg: FLOW50"
                        name="couponCode"
                        value={values.couponCode}
                        onChange={(e) =>
                           setValues({ ...values, couponCode: e.target.value })
                        }
                     />
                  </ContentCard>
                  <motion.div
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.9 }}
                     className="space-y-2"
                  >
                     <Group align="center">
                        <Checkbox
                           size="xs"
                           checked={isTermsAccepted}
                           onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <a
                              className="text-[#A684FF]"
                              href="https://forward-flow.com/terms"
                              target="_blank"
                           >
                              Terms & Conditions
                           </a>
                        </p>
                     </Group>
                     <Group>
                        <Checkbox
                           size="xs"
                           checked={isPolicyAccepted}
                           onChange={() =>
                              setIsPolicyAccepted(!isPolicyAccepted)
                           }
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <a
                              className="text-[#A684FF]"
                              href="https://forward-flow.com/privacy"
                              target="_blank"
                           >
                              Privacy policy
                           </a>
                        </p>
                     </Group>
                     <Group>
                        <Checkbox
                           size="xs"
                           checked={isTokenUsagePolicyAccepted}
                           onChange={() =>
                              setIsTokenUsagePolicyAccepted(
                                 !isTokenUsagePolicyAccepted
                              )
                           }
                        />
                        <p className="text-white/90 text-sm font-semibold">
                           I agree to the{' '}
                           <span className="text-[#A684FF]">
                              Token Usage Policy
                           </span>
                        </p>
                     </Group>
                  </motion.div>
               </>
            )}
         </Stack>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     loading={isLoading}
                     className="w-full"
                     onClick={() => {
                        if (isSubscriptionActivated) {
                           getNextStep()
                        } else {
                           handleSubmit()
                        }
                     }}
                     disabled={
                        isSubscriptionActivated
                           ? false
                           : !isTermsAccepted ||
                             !isPolicyAccepted ||
                             !isTokenUsagePolicyAccepted ||
                             !values.nameOnCard ||
                             !values.city ||
                             (values.billingZIPCode?.trim()?.length ?? 0) !== 5
                     }
                  >
                     {isSubscriptionActivated ? 'Next' : 'Subscribe'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}

/**
 * PaymentDetails component wrapped with Stripe Elements provider.
 * This ensures Stripe context is available only for this specific step.
 */
export const PaymentDetails = () => {
   return (
      <Elements stripe={stripePromise}>
         <PaymentDetailsForm />
      </Elements>
   )
}
